import { getErrorMessage } from "@/utils/errorHelpers";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authAPI } from "@/services/api";
import { wakeBackend } from "@/services/backendWake";
import { toast } from "sonner";
import PublicContactFaqSection from "@/components/app/PublicContactFaqSection";
import { Home, LockKeyhole, Mail, ShieldCheck } from "lucide-react";

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    wakeBackend({ force: true, reason: "login" });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      const { token, user } = response.data;
      onLogin(token, user);
      toast.success("Connexion reussie !");
      navigate(user?.role !== "admin" && !user?.email_verified ? "/verify-email" : "/dashboard");
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur de connexion"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-dvh bg-[#FCFBF8] text-[#1A1D1C]">
      <div className="mx-auto grid min-h-dvh w-full max-w-6xl grid-cols-1 lg:grid-cols-[0.92fr_1.08fr]">
        <section className="hidden bg-[#2A4B46] px-10 py-12 text-white lg:flex lg:flex-col lg:justify-between">
          <div>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D4A373] text-lg font-black">
              M
            </div>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-white/65">MMJ Edition</p>
          </div>
          <div className="max-w-md">
            <h1 className="font-['Outfit'] text-5xl font-extrabold leading-tight">
              Accedez a votre espace ARTICO.
            </h1>
            <p className="mt-5 text-base leading-7 text-white/75">
              Retrouvez vos activations, vos contenus et votre progression dans un espace securise.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/8 p-4">
            <ShieldCheck className="text-[#D4A373]" size={24} />
            <p className="text-sm text-white/75">Connexion protegee par jeton securise.</p>
          </div>
        </section>

        <section className="flex min-h-dvh items-center justify-center px-4 py-6 sm:px-6 lg:px-12">
          <div className="w-full max-w-md">
            <Link to="/">
              <Button variant="ghost" className="mb-4 text-[#2A4B46]">
                <Home size={16} className="mr-2" /> Accueil
              </Button>
            </Link>
            <div className="mb-7 lg:hidden">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#2A4B46] text-base font-black text-white">
                  M
                </div>
                <div>
                  <p className="text-lg font-extrabold text-[#2A4B46] font-['Outfit']">MMJ</p>
                  <p className="text-xs font-medium text-[#5C625F]">Maison d'edition</p>
                </div>
              </div>
            </div>

            <Card className="w-full rounded-2xl border-[#E6E4E0] bg-white shadow-sm sm:shadow-lg" data-testid="login-card">
              <CardHeader className="space-y-2 px-5 pb-3 pt-5 sm:px-7 sm:pt-7">
                <CardTitle className="text-2xl font-bold text-[#2A4B46] font-['Outfit'] sm:text-3xl" data-testid="login-title">
                  Connexion
                </CardTitle>
                <CardDescription className="text-sm leading-6 text-[#5C625F]">
                  Connectez-vous a votre compte MMJ
                </CardDescription>
              </CardHeader>
              <CardContent className="px-5 pb-5 sm:px-7 sm:pb-7">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-[#2A4B46]">Email</Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8B928E]" size={18} />
                      <Input
                        id="email"
                        type="email"
                        data-testid="login-email-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        autoComplete="email"
                        className="h-12 rounded-xl border-[#DAD7D1] bg-[#FCFBF8] pl-10 text-base focus-visible:ring-[#D4A373]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="password" className="text-sm font-semibold text-[#2A4B46]">Mot de passe</Label>
                      <Link to="/forgot-password" className="text-xs font-semibold text-[#2A4B46] hover:underline">
                        Oublie ?
                      </Link>
                    </div>
                    <div className="relative">
                      <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8B928E]" size={18} />
                      <Input
                        id="password"
                        type="password"
                        data-testid="login-password-input"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        autoComplete="current-password"
                        className="h-12 rounded-xl border-[#DAD7D1] bg-[#FCFBF8] pl-10 text-base focus-visible:ring-[#D4A373]"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={loading}
                    className="h-12 w-full rounded-xl bg-[#2A4B46] text-base font-semibold text-white hover:bg-[#1f3a36]"
                  >
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>

              <div className="mt-5 text-center text-sm text-[#5C625F]">
                Pas encore de compte ?{" "}
                <Link to="/register" className="font-semibold text-[#2A4B46] hover:underline" data-testid="register-link">
                  S'inscrire
                </Link>
              </div>
            </CardContent>
            </Card>
          </div>
        </section>
      </div>
      <PublicContactFaqSection compact className="bg-[#FCFBF8]" />
    </main>
  );
}
